/* sz-rd-offer.js — open/close for the "Make an offer" modal.
 *
 * ⚠️ THIS FILE MUST NEVER TOUCH THE FORM'S SUBMIT EVENT.
 *
 * The offer form is a native {% form 'contact' %} POST: the browser validates
 * the `required` fields, posts, and the page reloads. GOTCHAS #75 is exactly
 * this trap in reverse — a `required` field behind a JS-intercepted submit
 * means the browser blocks the submit BEFORE the handler runs, so nothing is
 * sent and nothing appears broken. Leaving submit alone is the feature, not an
 * omission. scripts/verify-offer.mjs fails the gate if a submit listener
 * appears in the snippet.
 *
 * Everything here is progressive enhancement: with JS off the modal never
 * opens, and the buy row above it still works normally.
 *
 * WHAT IT DOES DO, since 2026-08-21: keep the modal on the SELECTED GRADE.
 * Every field in the form is Liquid-rendered once, and sz-rd.js repaints the
 * buy row client-side, so a buyer who picked the $200 Collector grade was
 * sending an offer stamped with the $40 entry variant, its SKU and its price.
 * The merchant could not see the mismatch, because the email looked complete.
 * The repaint listens for `sz-rd:variant` and rewrites four hidden inputs and
 * two pieces of visible text. It is still nowhere near submit.
 *
 * AND, since the owner's 2026-08-21 night ruling: one offer per item per
 * 24 hours. The rules live in assets/offer-cooldown.js (pure, prover-run);
 * this file only owns localStorage and swaps the form for the wait panel at
 * OPEN time — never at submit (see the warning above; #75 still applies).
 */
(function () {
  'use strict';

  var FOCUSABLE = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  function init(root) {
    var backdrop = root.querySelector('[data-sz-offer-backdrop]');
    var openBtn = root.querySelector('[data-sz-offer-open]');
    var panel = backdrop && backdrop.querySelector('[role="dialog"]');
    if (!backdrop || !openBtn || !panel) return;

    /* OFFER-SLOT-01 (owner 2026-10-07): the offer line sits directly under Add to bag.
     * The snippet has to render OUTSIDE the product form (it carries its own contact
     * form, and forms cannot nest in HTML), so a section that wants the line inside
     * the buy box leaves an empty [data-sz-offer-slot] there and the line moves into
     * it. Only the line moves; the modal and its form stay put. `line` is kept as a
     * reference because root.querySelector can no longer find it after the move. */
    var line = root.querySelector('.sz-rd-offer__line');
    var slot = document.querySelector('[data-sz-offer-slot="' + root.getAttribute('data-sz-offer-pid') + '"]');
    if (line && slot) {
      slot.appendChild(line);
      slot.hidden = root.hidden;
      root.classList.add('is-slotted');
    }

    var lastFocused = null;

    /* ---------- one offer per item per 24 hours ----------
     *
     * Resolved at CALL time, never at module load — the dispatch timer threw
     * on every PDP the day it dereferenced window.SZDispatch at the top of
     * its file. Everything here fails OPEN: no cooldown module, no storage
     * (private mode), no pid — the form simply behaves as before. */
    var CD_KEY = 'sz:offer-sent';
    var pid = root.getAttribute('data-sz-offer-pid') || '';
    var waitEl = panel.querySelector('[data-sz-offer-wait]');
    var formEl = panel.querySelector('form');

    function cooldown() { return window.SZOfferCooldown || null; }

    function loadMap() {
      try { return JSON.parse(window.localStorage.getItem(CD_KEY)) || {}; }
      catch (e) { return {}; }
    }
    function saveMap(map) {
      try { window.localStorage.setItem(CD_KEY, JSON.stringify(map)); }
      catch (e) { /* storage unavailable: the rule simply doesn't bite */ }
    }

    /* Swap form/wait to match the stamp. The confirmation view always wins —
       the reload straight after a send carries BOTH a fresh stamp and the
       "Offer sent" panel, and covering the confirmation with "please wait"
       would read as the offer having failed. */
    function applyCooldownView() {
      if (!waitEl || !formEl || !pid) return;
      var cd = cooldown();
      if (!cd || panel.querySelector('[data-sz-offer-done]')) {
        if (waitEl) waitEl.hidden = true;
        return;
      }
      var until = cd.blockedUntil(loadMap(), pid, Date.now());
      waitEl.hidden = !until;
      formEl.hidden = !!until;
      if (until) {
        var when = waitEl.querySelector('[data-sz-offer-wait-when]');
        if (when) when.textContent = cd.waitText(until - Date.now());
      }
    }

    function focusables() {
      return Array.prototype.filter.call(
        panel.querySelectorAll(FOCUSABLE),
        function (el) { return el.offsetParent !== null; }
      );
    }

    function open() {
      applyCooldownView();
      lastFocused = document.activeElement;
      backdrop.hidden = false;
      /* Lock the page behind the modal. Storing the previous value rather than
         assuming '' means we hand back whatever the theme had set. */
      root._szPrevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      openBtn.setAttribute('aria-expanded', 'true');

      var first = panel.querySelector('[data-sz-offer-done]')
        || (waitEl && !waitEl.hidden ? waitEl : focusables()[0]);
      if (first) first.focus();
    }

    function close() {
      backdrop.hidden = true;
      document.body.style.overflow = root._szPrevOverflow || '';
      openBtn.setAttribute('aria-expanded', 'false');
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    }

    openBtn.setAttribute('aria-expanded', 'false');
    openBtn.addEventListener('click', open);

    /* ---------- keep the offer on the selected grade ----------
     *
     * The map is this snippet's own (title and sku are not in sz-rd.js's
     * shared [data-sz-rd-variants] payload), keyed by variant id, and it is
     * rendered only on pages where the button rendered.
     *
     * Every write below is guarded: after a successful post Liquid replaces the
     * whole fieldset with the confirmation, so on that reload NONE of these
     * elements exist. A repaint that assumed them would throw inside a variant
     * click handler and take the rest of the page's listeners down with it. */
    var variants = {};
    var mapEl = root.querySelector('[data-sz-offer-variants]');
    if (mapEl) {
      try { variants = JSON.parse(mapEl.textContent) || {}; } catch (e) { variants = {}; }
    }

    function setText(sel, text) {
      var el = root.querySelector(sel);
      if (el) el.textContent = text;
    }
    function setValue(sel, value) {
      var el = root.querySelector(sel);
      if (el) el.value = value;
    }

    /* IN STOCK ONLY, RE-TESTED PER GRADE (owner, 2026-08-21).
     *
     * Liquid decides this once, for whichever grade the page opened on. Grades
     * switch client-side, and on these lines the $200 grade is a PRE-ORDER
     * while the $45 entry grade is in stock — so the correct answer changes
     * without a page load, in both directions.
     *
     * `avail`/`pre` come from the snippet's own map, computed by the same
     * Liquid test the buy row uses, so this cannot drift from what Add to cart
     * is doing.
     *
     * Closing an OPEN modal on the way out is deliberate: leaving it up would
     * let someone finish and send an offer for a grade that has no animals. */
    function applyAvailability(v) {
      var eligible = !!(v && v.avail && !v.pre);
      if (!eligible && !backdrop.hidden) close();
      root.hidden = !eligible;
      if (slot && line && slot.contains(line)) slot.hidden = !eligible;
      return eligible;
    }

    function repaint(id) {
      var v = variants[String(id)];
      if (!v) return;
      if (!applyAvailability(v)) return;

      setValue('[data-sz-offer-f-variant]', v.title || '');
      setValue('[data-sz-offer-f-price]', v.priceFull || v.price || '');
      setValue('[data-sz-offer-f-sku]', v.sku || '');

      /* "Default Title" is Shopify's name for a product with no options. It is
         a database artefact, not something to show a customer. */
      var name = v.title && v.title.indexOf('Default') === -1 ? ' (' + v.title + ')' : '';
      setText('[data-sz-offer-vname]', name);
      setText('[data-sz-offer-listed]', v.price || '');

      /* PLACEHOLDER only — never `value`. Moving a number the buyer already
         typed, because they went back to compare grades, would be a small
         theft of their attention at the exact moment they are deciding. */
      var amount = root.querySelector('[data-sz-offer-amount]');
      if (amount && v.amount) amount.placeholder = v.amount;
    }

    /* Delegated on document: the event bubbles from the PDP root, and this
       snippet sits inside it, so either target would work — document keeps
       this file from having to know the PDP's structure. */
    document.addEventListener('sz-rd:variant', function (e) {
      if (e && e.detail && e.detail.id != null) repaint(e.detail.id);
    });

    var closeBtn = panel.querySelector('[data-sz-offer-close]');
    if (closeBtn) closeBtn.addEventListener('click', close);
    var doneBtn = panel.querySelector('[data-sz-offer-close-done]');
    if (doneBtn) doneBtn.addEventListener('click', close);

    /* ---------- OFFER-UI-02: "Sending" state on the button ----------
     * A CLICK listener, never submit (#75). It only paints: no preventDefault,
     * no disabled attribute (disabling a submit button inside its own click can
     * cancel the submission). It paints only when the browser already considers
     * the form valid, so an invalid form still shows the browser's own messages.
     * Shopify's invisible captcha can hold the post for a moment, and this is
     * what tells the buyer it is on its way. If the page has not moved after
     * 25 seconds the button resets and says so, so it can never sit spinning. */
    var sendBtn = panel.querySelector('[data-sz-offer-send]');
    var statusEl = panel.querySelector('[data-sz-offer-status]');
    if (sendBtn && formEl) {
      sendBtn.addEventListener('click', function () {
        if (typeof formEl.checkValidity === 'function' && !formEl.checkValidity()) return;
        window.setTimeout(function () {
          sendBtn.classList.add('is-sending');
          sendBtn.setAttribute('aria-busy', 'true');
          if (statusEl) statusEl.textContent = 'Sending your offer';
        }, 0);
        window.setTimeout(function () {
          if (!sendBtn.classList.contains('is-sending')) return;
          sendBtn.classList.remove('is-sending');
          sendBtn.removeAttribute('aria-busy');
          if (statusEl) statusEl.textContent = 'This is taking longer than usual. Please try again, or WhatsApp us.';
        }, 25000);
      });
    }

    /* Click the backdrop itself — not a click that merely bubbled out of the
       panel — to dismiss. Comparing e.target to backdrop is what distinguishes
       them; a listener without that check closes the modal when the user
       finishes selecting text inside it. */
    backdrop.addEventListener('mousedown', function (e) {
      if (e.target === backdrop) close();
    });

    document.addEventListener('keydown', function (e) {
      if (backdrop.hidden) return;

      if (e.key === 'Escape') {
        close();
        return;
      }

      /* Focus trap. Without it, tabbing walks out of the dialog and into the
         page behind, which for a screen-reader user is indistinguishable from
         the modal having closed. */
      if (e.key !== 'Tab') return;
      var items = focusables();
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    /* After a successful post Shopify reloads the product page and Liquid
       renders the confirmation in place of the fields. Without this the buyer
       lands on what looks like an untouched product page and cannot tell
       whether the offer went anywhere. The same render is the ONLY proof a
       send happened, so it is also where the 24-hour stamp is written —
       record() refuses to re-stamp, so reloading this page later doesn't
       extend the lock. */
    /* ---------- OFFER-CONFIRM-01: "we got your offer" email ----------
     * Still nowhere near submit (#75). While the buyer types, a copy of the
     * fields goes to sessionStorage on input/change. When Shopify renders the
     * success panel, that copy is POSTed to the app (/apps/auction/offer),
     * which fires the Klaviyo confirmation, and the copy is cleared, so a
     * later reload of the success page sends nothing. Fails silent. */
    var DRAFT_KEY = 'sz:offer-draft';
    function readDraftFromForm() {
      if (!formEl) return null;
      var d = { productId: pid, lines: [], email: '', name: '', offerTotal: '', note: '' };
      Array.prototype.forEach.call(formEl.elements, function (el) {
        var n = el.name || '';
        var v = (el.value || '').trim();
        if (n === 'contact[email]') d.email = v;
        else if (n === 'contact[Name]') d.name = v;
        else if (n === 'contact[Offer budget total]') d.offerTotal = v;
        else if (n === 'contact[body]') d.note = v;
        else {
          var m = n.match(/^contact\[Quantity (.+)\]$/);
          if (m && Number(v) > 0) d.lines.push({ grade: m[1], qty: Number(v) });
        }
      });
      return d;
    }
    if (formEl) {
      var stash = function () {
        try { window.sessionStorage.setItem(DRAFT_KEY, JSON.stringify(readDraftFromForm())); }
        catch (e) { /* no storage: no confirmation email, offer still sends */ }
      };
      formEl.addEventListener('input', stash);
      formEl.addEventListener('change', stash);
    }
    function takeDraft() {
      var d = null;
      try { d = JSON.parse(window.sessionStorage.getItem(DRAFT_KEY)); window.sessionStorage.removeItem(DRAFT_KEY); }
      catch (e) { d = null; }
      return d && d.productId === pid ? d : null;
    }

    /* OFFER-UI-02: show the buyer what they sent, from the same draft copy the
       confirmation email uses. Built with textContent only (buyer-typed text). */
    function showRecap(d) {
      var dl = panel.querySelector('[data-sz-offer-recap]');
      if (!dl || !d) return;
      var rows = [];
      (d.lines || []).forEach(function (l) { rows.push([l.grade.indexOf('Default') === -1 ? l.grade : 'Quantity', String(l.qty)]); });
      if (d.offerTotal) rows.push(['Your offer', '$' + d.offerTotal]);
      if (d.email) rows.push(['Reply to', d.email]);
      if (!rows.length) return;
      rows.forEach(function (r) {
        var wrap = document.createElement('div');
        var dt = document.createElement('dt'); dt.textContent = r[0];
        var dd = document.createElement('dd'); dd.textContent = r[1];
        wrap.appendChild(dt); wrap.appendChild(dd); dl.appendChild(wrap);
      });
      dl.hidden = false;
    }

    function markSent() {
      root.classList.add('is-sent');
      var btn = openBtn;
      if (!line || !btn) return;
      var note = document.createElement('span');
      note.className = 'sz-rd-offer__sentnote';
      note.textContent = 'Offer sent. We\u2019ll reply by email. ';
      line.textContent = '';
      line.appendChild(note);
      btn.textContent = 'View';
      line.appendChild(btn);
    }

    function sendDraft(d) {
      if (!d || !d.email || !window.fetch) return;
      try {
        window.fetch('/apps/auction/offer', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(d),
          keepalive: true,
        }).catch(function () {});
      } catch (e) { /* ignore */ }
    }

    if (panel.querySelector('[data-sz-offer-done]')) {
      var cdBoot = cooldown();
      if (cdBoot && pid) saveMap(cdBoot.record(loadMap(), pid, Date.now()));
      var draft = takeDraft();
      showRecap(draft);
      sendDraft(draft);
      markSent();
      open();
    }
  }

  function boot() {
    var nodes = document.querySelectorAll('[data-sz-offer]');
    Array.prototype.forEach.call(nodes, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
